/**
=========================================================
* Iris Design Limited  Fulfilment POD - HTML DataTable Component
=========================================================
* Lightweight, pure HTML/CSS DataTable with all features
*/

import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import "./styles.css";

function HTMLDataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
}) {
  const defaultValue = entriesPerPage?.defaultValue || 500;
  const entries = entriesPerPage?.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["500"];

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  // State management
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultValue);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((row) => {
      return columns.some((column) => {
        const cellValue = row[column.accessor];
        if (cellValue === null || cellValue === undefined) return false;

        // Handle React elements
        if (typeof cellValue === 'object' && cellValue.props) {
          const textContent = extractTextFromReactElement(cellValue);
          return textContent.toLowerCase().includes(searchLower);
        }

        return String(cellValue).toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle React elements
      const aText = typeof aValue === 'object' && aValue.props
        ? extractTextFromReactElement(aValue)
        : aValue;
      const bText = typeof bValue === 'object' && bValue.props
        ? extractTextFromReactElement(bValue)
        : bValue;

      if (aText === null || aText === undefined) return 1;
      if (bText === null || bText === undefined) return -1;

      const comparison = String(aText).localeCompare(String(bText), undefined, {
        numeric: true,
        sensitivity: 'base'
      });

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, pageIndex, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const pageOptions = Array.from({ length: totalPages }, (_, i) => i);

  // Set default page size on mount
  useEffect(() => {
    setPageSize(defaultValue);
  }, [defaultValue]);

  // Reset to first page when search or sort changes
  useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, sortConfig]);

  // Handle sorting
  const handleSort = (accessor) => {
    if (!isSorted) return;

    setSortConfig((prev) => {
      if (prev.key === accessor) {
        if (prev.direction === 'asc') {
          return { key: accessor, direction: 'desc' };
        } else {
          return { key: null, direction: null };
        }
      }
      return { key: accessor, direction: 'asc' };
    });
  };

  // Get sort icon
  const getSortIcon = (accessor) => {
    if (!isSorted) return null;
    if (sortConfig.key !== accessor) {
      return <span className="sort-icon">⇅</span>;
    }
    return sortConfig.direction === 'asc'
      ? <span className="sort-icon sort-asc">↑</span>
      : <span className="sort-icon sort-desc">↓</span>;
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 0 && page < totalPages) {
      setPageIndex(page);
    }
  };

  const handlePageInput = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 1 && value <= totalPages) {
      goToPage(value - 1);
    } else {
      goToPage(0);
    }
  };

  // Calculate entries range
  const entriesStart = pageIndex === 0 ? 1 : pageIndex * pageSize + 1;
  const entriesEnd = Math.min((pageIndex + 1) * pageSize, sortedData.length);

  // Extract text from React elements for search
  function extractTextFromReactElement(element) {
    if (typeof element === 'string' || typeof element === 'number') {
      return String(element);
    }
    if (element?.props?.children) {
      if (Array.isArray(element.props.children)) {
        return element.props.children.map(extractTextFromReactElement).join(' ');
      }
      return extractTextFromReactElement(element.props.children);
    }
    return '';
  }

  return (
    <div className="html-data-table-container">
      {/* Controls Bar */}
      {(entriesPerPage || canSearch) && (
        <div className="html-data-table-controls">
          {entriesPerPage && (
            <div className="entries-per-page">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageIndex(0);
                }}
                className="entries-select"
              >
                {entries.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
              <span className="entries-label">entries per page</span>
            </div>
          )}
          {canSearch && (
            <div className="search-container">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="html-data-table-wrapper">
        <table className="html-data-table">
          <thead>
            <tr>
              {columns.map((column, idx) => {
                const accessor = column.accessor || idx;
                // Check if Header is a React element (object with props or isValidElement)
                const isHeaderReactElement = column.Header &&
                  (React.isValidElement(column.Header) ||
                    (typeof column.Header === 'object' && column.Header !== null && !Array.isArray(column.Header) && column.Header.props));
                const isSortable = isSorted && column.accessor && !isHeaderReactElement;

                return (
                  <th
                    key={idx}
                    style={{
                      width: column.width || 'auto',
                      textAlign: column.align || 'left',
                      cursor: isSortable ? 'pointer' : 'default',
                    }}
                    onClick={(e) => {
                      // Only handle sort if clicking on the header itself, not on child elements
                      if (isSortable && e.target === e.currentTarget) {
                        handleSort(accessor);
                      }
                    }}
                    className={isSortable ? 'sortable-header' : ''}
                  >
                    {isHeaderReactElement ? (
                      // If Header is a React element, render it directly
                      <div onClick={(e) => e.stopPropagation()} style={{ display: 'block' }}>
                        {column.Header}
                      </div>
                    ) : (
                      // If Header is simple text, use wrapper with sort icon
                      <div className="header-content">
                        {column.render ? column.render('Header') : column.Header}
                        {isSortable && getSortIcon(accessor)}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="no-data">
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.map((row, key) => {
                // Improved color scheme for Date column based on order status
                const rowBackgroundColor =
                  row.cancel === "cancel" || row.order_status === "error"
                    ? "#ffcdd2"  // Light red for cancelled/error orders
                    : row.cancel === "refund" || row.order_status === "refund"
                      ? "#ffe0b2"  // Light orange for refunded orders
                      : row.order_status === "case_item" || row.order_status === "case_des"
                        ? "#b2ebf2"  // Light cyan for case items
                        : row.order_status === "delivered"
                          ? "#c8e6c9"  // Light green for delivered orders
                          : row.itemEditor === row.id
                            ? "#C2DC9F"  // Light green for selected/editing
                            : undefined;

                const isLastRow = key === paginatedData.length - 1;
                const shouldHideBorder = noEndBorder && key === sortedData.length - 1;

                // Determine if this is the Date column
                const isDateColumn = (column, idx) => {
                  return column.accessor === "date" || column.Header === "Date" || (typeof column.Header === 'string' && column.Header.toLowerCase() === "date");
                };

                return (
                  <tr key={key}>
                    {columns.map((column, idx) => {
                      const accessor = column.accessor || idx;
                      const cellValue = row[accessor];
                      // Check if cellValue is a React element
                      const isCellReactElement = cellValue &&
                        (React.isValidElement(cellValue) ||
                          (typeof cellValue === 'object' && cellValue !== null && !Array.isArray(cellValue) && cellValue.props));

                      // Only apply background to Date column
                      const isDateCol = isDateColumn(column, idx);
                      const cellBackgroundColor = isDateCol
                        ? (row.itemEditor === row.id ? "#C2DC9F" : rowBackgroundColor)
                        : undefined;

                      return (
                        <td
                          key={idx}
                          style={{
                            textAlign: column.align || 'left',
                            borderBottom: shouldHideBorder ? 'none' : undefined,
                            backgroundColor: cellBackgroundColor,
                          }}
                          className={`${isLastRow && noEndBorder ? 'no-end-border' : ''} ${isDateCol ? 'date-column' : ''}`}
                          data-date-column={isDateCol}
                        >
                          {column.render
                            ? column.render(cellValue, row)
                            : isCellReactElement ? (
                              <div style={{ display: 'block' }}>
                                {cellValue}
                              </div>
                            ) : (
                              cellValue
                            )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="html-data-table-footer">
        {showTotalEntries && (
          <div className="total-entries">
            Showing {entriesStart} to {entriesEnd} of {sortedData.length} entries
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => goToPage(pageIndex - 1)}
              disabled={pageIndex === 0}
            >
              ‹
            </button>

            {pageOptions.length <= 6 ? (
              pageOptions.map((option) => (
                <button
                  key={option}
                  className={`pagination-btn ${pageIndex === option ? 'active' : ''}`}
                  onClick={() => goToPage(option)}
                >
                  {option + 1}
                </button>
              ))
            ) : (
              <>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageIndex + 1}
                  onChange={handlePageInput}
                  className="page-input"
                />
              </>
            )}

            <button
              className="pagination-btn"
              onClick={() => goToPage(pageIndex + 1)}
              disabled={pageIndex >= totalPages - 1}
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Default props
HTMLDataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// PropTypes
HTMLDataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default HTMLDataTable;
